<template>
  <div class="cashiers-page">
    <header class="cashiers-page__header">
      <div>
        <h1>{{ $t('cashiers.title') }}</h1>
        <p>{{ $t('cashiers.subtitle') }}</p>
      </div>
      <button type="button" class="cashiers-page__btn" @click="openCreate">
        <i class="mdi mdi-account-plus-outline"></i>
        {{ $t('cashiers.add') }}
      </button>
    </header>

    <div v-if="cashiers.length" class="cashiers-page__grid">
      <article v-for="cashier in cashiers" :key="cashier.id" class="cashier-card">
        <div class="cashier-card__top">
          <div class="cashier-card__icon" :class="{ 'cashier-card__icon--admin': cashier.role === 'admin' }">
            <i :class="['mdi', cashier.role === 'admin' ? 'mdi-shield-account-outline' : 'mdi-account-outline']"></i>
          </div>
          <div v-if="cashier.role !== 'admin'" class="cashier-card__actions">
            <button
              type="button"
              class="cashier-card__icon-btn"
              :title="$t('cashiers.edit')"
              @click="openEdit(cashier)"
            >
              <i class="mdi mdi-pencil-outline"></i>
            </button>
            <button
              type="button"
              class="cashier-card__icon-btn cashier-card__icon-btn--danger"
              :title="$t('cashiers.delete')"
              @click="askDelete(cashier)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
          </div>
        </div>

        <h3>{{ cashier.name || $t('cashiers.unnamed') }}</h3>
        <span class="cashier-card__email">{{ cashier.email }}</span>

        <div class="cashier-card__badges">
          <span
            class="cashier-card__badge"
            :class="cashier.role === 'admin' ? 'cashier-card__badge--admin' : 'cashier-card__badge--cashier'"
          >
            {{ cashier.role === 'admin' ? $t('cashiers.administrator') : $t('cashiers.cashier') }}
          </span>
          <span
            v-if="cashier.role !== 'admin' && cashier.can_manage_products"
            class="cashier-card__badge cashier-card__badge--rights"
          >
            {{ $t('cashiers.productsAndStock') }}
          </span>
        </div>
      </article>
    </div>

    <div v-else class="cashiers-page__empty">
      <i class="mdi mdi-account-group-outline"></i>
      <p>{{ $t('cashiers.emptyTitle') }}</p>
      <span>{{ $t('cashiers.emptyText') }}</span>
    </div>

    <v-dialog v-model="formOpen" max-width="480">
      <v-card class="cashiers-page__dialog">
        <v-card-title>{{ isEdit ? $t('cashiers.editTitle') : $t('cashiers.addTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" :label="$t('cashiers.name')" outlined dense class="mb-2" :placeholder="$t('cashiers.namePlaceholder')" />
          <v-text-field
            v-model="form.email"
            :label="$t('cashiers.email')"
            outlined
            dense
            class="mb-2"
            :disabled="isEdit"
            placeholder="kassir1@magazin.kz"
          />
          <v-text-field
            v-model="form.password"
            :label="isEdit ? $t('cashiers.newPassword') : $t('cashiers.password')"
            outlined
            dense
            class="mb-2"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showPassword = !showPassword"
          />
          <v-switch
            v-model="form.can_manage_products"
            color="#0b4f8a"
            hide-details
            class="mt-1"
            :label="$t('cashiers.permission')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="formOpen = false">{{ $t('cashiers.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="saving" @click="save">{{ $t('cashiers.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteOpen" max-width="420">
      <v-card class="cashiers-page__dialog">
        <v-card-title>{{ $t('cashiers.deleteTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('cashiers.deleteText', { name: deleteName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteOpen = false">{{ $t('cashiers.cancel') }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">{{ $t('cashiers.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  createCashier,
  deleteCashier,
  getCashiers,
  updateCashier,
} from '@/api/cashiers'
import { notify, notifyError } from '@/utils/notify'

export default {
  name: 'CashierControl',
  data() {
    return {
      cashiers: [],
      formOpen: false,
      isEdit: false,
      cashierId: null,
      showPassword: false,
      form: {
        name: '',
        email: '',
        password: '',
        can_manage_products: false,
      },
      saving: false,
      deleteOpen: false,
      deleteName: '',
      deleting: false,
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      try {
        this.cashiers = await getCashiers()
      } catch (error) {
        notifyError(error)
      }
    },
    openCreate() {
      this.isEdit = false
      this.cashierId = null
      this.showPassword = false
      this.form = { name: '', email: '', password: '', can_manage_products: false }
      this.formOpen = true
    },
    openEdit(cashier) {
      this.isEdit = true
      this.cashierId = cashier.id
      this.showPassword = false
      this.form = {
        name: cashier.name || '',
        email: cashier.email,
        password: '',
        can_manage_products: !!cashier.can_manage_products,
      }
      this.formOpen = true
    },
    askDelete(cashier) {
      this.cashierId = cashier.id
      this.deleteName = cashier.name || cashier.email
      this.deleteOpen = true
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await deleteCashier(this.cashierId)
        notify(this.$t('cashiers.deleted'), 'warning')
        this.deleteOpen = false
        await this.load()
      } catch (error) {
        notifyError(error, this.$t('cashiers.deleteFailed'))
      } finally {
        this.deleting = false
      }
    },
    async save() {
      if (!this.form.name) {
        notify(this.$t('cashiers.enterName'), 'warning')
        return
      }
      if (!this.isEdit && !this.form.email) {
        notify(this.$t('cashiers.enterEmail'), 'warning')
        return
      }
      if (!this.isEdit && (!this.form.password || this.form.password.length < 6)) {
        notify(this.$t('cashiers.passwordMin'), 'warning')
        return
      }
      if (this.isEdit && this.form.password && this.form.password.length < 6) {
        notify(this.$t('cashiers.passwordMin'), 'warning')
        return
      }

      this.saving = true
      try {
        const result = this.isEdit
          ? await updateCashier(this.cashierId, {
              name: this.form.name,
              password: this.form.password || null,
              can_manage_products: this.form.can_manage_products,
            })
          : await createCashier({
              name: this.form.name,
              email: this.form.email,
              password: this.form.password,
              can_manage_products: this.form.can_manage_products,
            })

        notify(result.message || this.$t('cashiers.saved'))
        this.formOpen = false
        await this.load()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.cashiers-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.cashiers-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.cashiers-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.2s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #093f6d;
  }
}

.cashiers-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}

.cashier-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  transition: border-color 0.2s ease, transform 0.15s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-2px);
  }

  h3 {
    margin: 4px 0 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
  }
}

.cashier-card__email {
  font-size: 13px;
  color: #6b7a90;
}

.cashier-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.cashier-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }

  &--admin {
    background: #0b4f8a;
    color: #fff;
  }
}

.cashier-card__actions {
  display: flex;
  gap: 6px;
}

.cashier-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  background: #fff;
  color: #3d4f63;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    border-color: #0b4f8a;
    color: #0b4f8a;
    background: #eef4fa;
  }

  &--danger {
    color: #b42318;

    &:hover {
      border-color: #f0b4ae;
      color: #b42318;
      background: #fff5f4;
    }
  }
}

.cashier-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.cashier-card__badge {
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 650;

  &--admin {
    background: #0b4f8a;
    color: #fff;
  }

  &--cashier {
    background: #e8f1f8;
    color: #0b4f8a;
  }

  &--rights {
    background: #e7f6ec;
    color: #1e7d3e;
  }
}

.cashiers-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: #152033;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.cashiers-page__dialog {
  border-radius: 16px !important;
}

@media (max-width: 720px) {
  .cashiers-page {
    padding: 20px 16px 32px;
  }

  .cashiers-page__header {
    flex-direction: column;
  }
}
</style>
